import { usePermission } from '@/hooks/usePermission';
import type { Permission } from '@/types/rbac';
import { ShieldAlert } from 'lucide-react';

interface RoleGuardProps {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * RoleGuard — renders children only if user has the required permission.
 */
export function RoleGuard({ permission, children, fallback }: RoleGuardProps) {
  const { allowed, loading } = usePermission(permission);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!allowed) {
    return (
      fallback ?? (
        <div className="flex flex-col items-center justify-center gap-4 p-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-light">
            <ShieldAlert className="h-6 w-6 text-error" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-800">Zugriff verweigert</h2>
          <p className="max-w-sm text-sm text-neutral-500">
            Du hast keine Berechtigung für diese Funktion. Bitte wende dich an deinen Administrator.
          </p>
        </div>
      )
    );
  }

  return <>{children}</>;
}
